import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2 } from "lucide-react";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const token = params.get("token");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invitation", { token, password });
      toast.success("Account activated!");
      if (data.token) { await completeLogin(data.token); navigate("/onboarding"); }
      else navigate("/login");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="mb-6"><Logo /></div>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-[#9ACC32]" />Accept your invitation</h2>
        {!token ? (
          <p className="text-sm text-[#C42607] mt-4">Missing or invalid invitation link. <Link to="/login" className="underline">Back to login</Link></p>
        ) : (
          <>
            <p className="text-sm text-slate-500 mt-1">Set a password to activate your ChitChat CRM account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="accept-invite-form">
              <div>
                <Label className="text-xs font-medium text-slate-600">New Password</Label>
                <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="invite-password" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Confirm Password</Label>
                <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="invite-confirm" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">{loading ? "Activating…" : "Activate Account"}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
